import {
  call,
  put,
  takeLatest,
  ForkEffect,
} from "redux-saga/effects";
import { INTERNAL_ERROR } from "#/infra/redux/actionType";
import { startAxios, setAxiosResult, resolveTimezone } from "#/infra/saga";
import * as actions from "#/modules/dashboard/redux/actionType";
import fetchLicenseApi, { LicenseType } from "#/apis/infra/getLicense";
import fetchStatisticsApi, { StatisticsType } from "#/apis/infra/getStatistics";
import fetchTraceRecordsApi, { TraceRecordsResponse } from "#/apis/infra/getTraceRecords";
import fetchStatsSummaryApi, { StatsSummaryResponse } from "#/apis/infra/getStatsSummary";
import fetchTraceDetailApi, { TraceDetailType } from "#/apis/infra/getTraceDetail";
import fetchDashboardApi, { DashboardResponse } from "#/apis/infra/getDashboard";
import fetchPolicyNameMappingApi, { PolicyNameMapping } from "#/apis/infra/getPolicyNameMapping";
import InternalResponse from "#/types/internalResponse";

const formatDate = (date: Date) => date.toISOString().replace(/\.\d{3}Z$/, "Z");

function* fetchLicense({ data }: { type: string, data: string }) {
  yield startAxios();

  try {
    const { success, msg, data: r }: InternalResponse<LicenseType> = yield call(fetchLicenseApi, {
      token: data,
    });

    if (success) {
      yield setAxiosResult({
        success: true,
        msg: "",
        detail: undefined,
      });

      yield put({
        type: actions.GOT_LICENSE,
        data: r,
      });
    } else {
      yield setAxiosResult({
        success: false,
        msg,
      });

      yield put({
        type: actions.GOT_LICENSE,
        data: null,
      });
    }
  } catch (error) {
    yield put({
      type: INTERNAL_ERROR,
      data: error,
    });
  }
}

/* eslint-disable @typescript-eslint/naming-convention, @typescript-eslint/no-explicit-any */
function* fetchUnsafeRequests({ data }: { type: string, data: { accessToken: string, service_token_id: string } }) {
  yield startAxios();

  if (!data?.accessToken) return;

  try {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);

    const params = {
      start_at: formatDate(startDate),
      end_at: formatDate(endDate),
      unsafe_count: 1000,
      error_count: 1000,
      service_token_id: data.service_token_id,
    };

    const { success, msg, data: r }: InternalResponse<StatisticsType> = yield call(
      fetchStatisticsApi,
      {
        params,
        token: data.accessToken,
      },
    );

    if (success) {
      yield setAxiosResult({
        success: true,
        msg: "",
        detail: undefined,
      });

      yield put({
        type: actions.GOT_UNSAFE_REQUESTS,
        data: r?.unsafe_request_records ?? r?.unsafe_requests ?? (r as any)?.malicious_requests ?? [],
      });
    } else {
      yield setAxiosResult({
        success: false,
        msg,
      });

      yield put({
        type: actions.GOT_UNSAFE_REQUESTS,
        data: null,
      });
    }
  } catch (error) {
    yield put({
      type: INTERNAL_ERROR,
      data: error,
    });
  }
}
/* eslint-enable @typescript-eslint/naming-convention, @typescript-eslint/no-explicit-any */

/* eslint-disable @typescript-eslint/naming-convention */
function* fetchStatsSummary({
  data,
}: {
  type: string;
  data: { accessToken: string; service_token_id?: string };
}) {
  if (!data?.accessToken) return;

  try {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);

    const params: Record<string, string> = {
      start_at: formatDate(startDate),
      end_at: formatDate(endDate),
    };
    if (data.service_token_id) params.service_token_id = data.service_token_id;

    const { success, data: r }: InternalResponse<StatsSummaryResponse> = yield call(
      fetchStatsSummaryApi,
      { params, token: data.accessToken } as any,
    );

    yield put({
      type: actions.GOT_STATS_SUMMARY,
      data: success ? r?.request_statistics : null,
    });
  } catch (error) {
    yield put({
      type: INTERNAL_ERROR,
      data: error,
    });
  }
}

function* fetchTraceRecords({
  data,
}: {
  type: string;
  data: {
    accessToken: string;
    service_token_id?: string;
    cursor?: string;
    limit?: number;
    violated_types?: string;
    violated_policy_names?: string;
  };
}) {
  if (!data?.accessToken) return;

  try {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);

    const params: Record<string, unknown> = {
      start_at: formatDate(startDate),
      end_at: formatDate(endDate),
      limit: data.limit ?? 20,
    };
    if (data.service_token_id) params.service_token_id = data.service_token_id;
    if (data.cursor) params.cursor = data.cursor;
    if (data.violated_types) params.violated_types = data.violated_types;
    if (data.violated_policy_names) params.violated_policy_names = data.violated_policy_names;

    const isAppend = !!data.cursor;

    const { success, data: r }: InternalResponse<TraceRecordsResponse> = yield call(
      fetchTraceRecordsApi,
      { params, token: data.accessToken } as any,
    );

    if (success && r) {
      yield put({
        type: actions.GOT_TRACE_RECORDS,
        data: {
          records: r.data?.unsafe_request_records ?? [],
          hasNext: r.pagination?.has_next ?? false,
          nextCursor: r.pagination?.next_cursor,
          append: isAppend,
        },
      });
    } else {
      yield put({
        type: actions.GOT_TRACE_RECORDS,
        data: { records: [], hasNext: false, nextCursor: undefined, append: false },
      });
    }
  } catch (error) {
    yield put({
      type: INTERNAL_ERROR,
      data: error,
    });
  }
}
/* eslint-enable @typescript-eslint/naming-convention */

function* fetchTraceDetail({ data }: { type: string, data: { accessToken: string, traceId: string } }) {
  if (!data?.accessToken || !data?.traceId) return;

  try {
    const { success, data: r }: InternalResponse<TraceDetailType> = yield call(fetchTraceDetailApi, {
      traceId: data.traceId,
      token: data.accessToken,
    });

    yield put({
      type: actions.GOT_TRACE_DETAIL,
      data: success ? r : null,
    });
  } catch (error) {
    yield put({
      type: INTERNAL_ERROR,
      data: error,
    });
  }
}

/* eslint-disable @typescript-eslint/naming-convention */
function* fetchDashboardData({
  data,
}: {
  type: string;
  data: { accessToken: string; period: number; timezone?: string };
}) {
  yield startAxios();

  if (!data?.accessToken) return;

  try {
    const timezone: string = yield resolveTimezone(data.timezone);

    const { success, msg, data: r }: InternalResponse<DashboardResponse> = yield call(
      fetchDashboardApi,
      {
        period: data.period,
        timezone,
        token: data.accessToken,
      },
    );

    if (success) {
      yield setAxiosResult({
        success: true,
        msg: "",
        detail: undefined,
      });

      yield put({
        type: actions.GOT_DASHBOARD_DATA,
        data: r,
      });
    } else {
      yield setAxiosResult({
        success: false,
        msg,
      });

      yield put({
        type: actions.GOT_DASHBOARD_DATA,
        data: null,
      });
    }
  } catch (error) {
    yield put({
      type: INTERNAL_ERROR,
      data: error,
    });
  }
}
/* eslint-enable @typescript-eslint/naming-convention */

function* fetchPolicyNameMapping({ data }: { type: string; data: string }) {
  try {
    const { success, data: r }: InternalResponse<PolicyNameMapping> = yield call(
      fetchPolicyNameMappingApi,
      { token: data },
    );

    yield put({
      type: actions.GOT_POLICY_NAME_MAPPING,
      data: success ? r : null,
    });
  } catch (error) {
    yield put({
      type: INTERNAL_ERROR,
      data: error,
    });
  }
}

export default function* dashboard(): Generator<ForkEffect<never>, void, unknown> {
  yield takeLatest(actions.GET_LICENSE, fetchLicense);
  yield takeLatest(actions.GET_UNSAFE_REQUESTS, fetchUnsafeRequests);
  yield takeLatest(actions.GET_STATS_SUMMARY, fetchStatsSummary);
  yield takeLatest(actions.GET_TRACE_RECORDS, fetchTraceRecords);
  yield takeLatest(actions.GET_TRACE_DETAIL, fetchTraceDetail);
  yield takeLatest(actions.GET_DASHBOARD_DATA, fetchDashboardData);
  yield takeLatest(actions.GET_POLICY_NAME_MAPPING, fetchPolicyNameMapping);
}
